/**
 * CashflowSummary - Summary cards showing income, expense, and net amounts
 */

import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';
import { useCashflowStore } from '../../stores/cashflowStore';
import {
  TrendingUp,
  TrendingDown,
  Wallet,
  Clock,
  CheckCircle,
  AlertTriangle,
  RefreshCw,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';

// Format currency with proper locale
const formatCurrency = (amount, currency = 'TRY') => {
  const formatter = new Intl.NumberFormat('tr-TR', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
  return formatter.format(amount);
};

const CashflowSummary = () => {
  const { summary, exchangeRates, pastDebts, pastDebtsTotalTRY, pastDebtsByCurrency, openEditModal } = useCashflowStore();
  const [isPastDebtsExpanded, setIsPastDebtsExpanded] = useState(false);

  // Check if we have multiple currencies
  const hasMultipleCurrencies = Object.keys(summary.byCurrency).length > 1;
  const hasPastDebts = pastDebts && pastDebts.length > 0;

  // Calculate total due (current month expenses + past debts)
  const totalDueTRY = summary.totalExpenseTRY + (pastDebtsTotalTRY || 0);
  const totalDue = summary.totalExpense + (pastDebtsByCurrency?.TRY || 0);

  const cards = [
    {
      title: 'Gelir',
      amount: hasMultipleCurrencies ? summary.totalIncomeTRY : summary.totalIncome,
      amountTRY: summary.totalIncomeTRY,
      currency: 'TRY',
      icon: TrendingUp,
      color: 'emerald',
      bgClass: 'bg-emerald-900/30',
      borderClass: 'border-emerald-700/50',
      textClass: 'text-emerald-400',
      iconBgClass: 'bg-emerald-500/20',
    },
    {
      title: 'Gider',
      amount: hasMultipleCurrencies ? summary.totalExpenseTRY : summary.totalExpense,
      amountTRY: summary.totalExpenseTRY,
      currency: 'TRY',
      icon: TrendingDown,
      color: 'red',
      bgClass: 'bg-red-900/30',
      borderClass: 'border-red-700/50',
      textClass: 'text-red-400',
      iconBgClass: 'bg-red-500/20',
    },
    {
      title: 'Net',
      amount: hasMultipleCurrencies ? summary.netAmountTRY : summary.netAmount,
      amountTRY: summary.netAmountTRY,
      currency: 'TRY',
      icon: Wallet,
      color: (hasMultipleCurrencies ? summary.netAmountTRY : summary.netAmount) >= 0 ? 'indigo' : 'amber',
      bgClass: (hasMultipleCurrencies ? summary.netAmountTRY : summary.netAmount) >= 0 ? 'bg-indigo-900/30' : 'bg-amber-900/30',
      borderClass: (hasMultipleCurrencies ? summary.netAmountTRY : summary.netAmount) >= 0 ? 'border-indigo-700/50' : 'border-amber-700/50',
      textClass: (hasMultipleCurrencies ? summary.netAmountTRY : summary.netAmount) >= 0 ? 'text-indigo-400' : 'text-amber-400',
      iconBgClass: (hasMultipleCurrencies ? summary.netAmountTRY : summary.netAmount) >= 0 ? 'bg-indigo-500/20' : 'bg-amber-500/20',
    },
  ];

  // Total overdue count includes current month + past months
  const totalOverdueCount = summary.overdueCount + (pastDebts?.length || 0);

  const statCards = [
    {
      title: 'Bekleyen',
      count: summary.pendingCount,
      icon: Clock,
      textClass: 'text-blue-400',
      iconBgClass: 'bg-blue-500/20',
    },
    {
      title: 'Tamamlanan',
      count: summary.completedCount,
      icon: CheckCircle,
      textClass: 'text-emerald-400',
      iconBgClass: 'bg-emerald-500/20',
    },
    {
      title: 'Gecikmiş',
      count: totalOverdueCount,
      icon: AlertTriangle,
      textClass: 'text-amber-400',
      iconBgClass: 'bg-amber-500/20',
    },
  ];

  return (
    <div className="space-y-4">
      {/* Main summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {cards.map((card, index) => (
          <motion.div
            key={card.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className={`${card.bgClass} border ${card.borderClass} rounded-xl p-4`}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-400">{card.title}</p>
                <p className={`text-2xl font-bold ${card.textClass} mt-1`}>
                  {formatCurrency(card.amount, card.currency)}
                </p>
              </div>
              <div className={`w-12 h-12 ${card.iconBgClass} rounded-xl flex items-center justify-center`}>
                <card.icon className={`w-6 h-6 ${card.textClass}`} />
              </div>
            </div>

            {/* Multi-currency breakdown */}
            {Object.keys(summary.byCurrency).length > 1 && card.title !== 'Net' && (
              <div className="mt-3 pt-3 border-t border-gray-700/50">
                <div className="flex flex-wrap gap-2">
                  {Object.entries(summary.byCurrency).map(([currency, data]) => {
                    if (currency === 'TRY') return null;
                    const value = card.title === 'Gelir' ? data.income : data.expense;
                    if (value === 0) return null;
                    return (
                      <span
                        key={currency}
                        className="text-xs text-gray-400 bg-gray-800/50 px-2 py-1 rounded"
                      >
                        {formatCurrency(value, currency)}
                      </span>
                    );
                  })}
                </div>
              </div>
            )}
          </motion.div>
        ))}
      </div>

      {/* Past debts and total due section */}
      {hasPastDebts && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Past Debts Card - Collapsible */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-orange-900/30 border border-orange-700/50 rounded-xl overflow-hidden"
          >
            {/* Header - clickable */}
            <button
              onClick={() => setIsPastDebtsExpanded(!isPastDebtsExpanded)}
              className="w-full p-4 text-left hover:bg-orange-900/20 transition-colors"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-400">Geçmiş Borçlar</p>
                  <p className="text-2xl font-bold text-orange-400 mt-1">
                    {formatCurrency(hasMultipleCurrencies ? pastDebtsTotalTRY : (pastDebtsByCurrency?.TRY || 0), 'TRY')}
                  </p>
                  <p className="text-xs text-orange-300/70 mt-1">
                    {pastDebts.length} ödenmemiş işlem
                  </p>
                </div>
                <div className="flex flex-col items-center gap-2">
                  <div className="w-12 h-12 bg-orange-500/20 rounded-xl flex items-center justify-center">
                    <AlertTriangle className="w-6 h-6 text-orange-400" />
                  </div>
                  {isPastDebtsExpanded ? (
                    <ChevronUp className="w-5 h-5 text-orange-400" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-orange-400" />
                  )}
                </div>
              </div>

              {/* Multi-currency breakdown for past debts */}
              {pastDebtsByCurrency && Object.keys(pastDebtsByCurrency).length > 1 && (
                <div className="mt-3 pt-3 border-t border-orange-700/30">
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(pastDebtsByCurrency).map(([currency, amount]) => {
                      if (currency === 'TRY' || amount === 0) return null;
                      return (
                        <span
                          key={currency}
                          className="text-xs text-orange-300 bg-orange-800/30 px-2 py-1 rounded"
                        >
                          {formatCurrency(amount, currency)}
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}
            </button>

            {/* Expanded content - list of past debts */}
            <AnimatePresence>
              {isPastDebtsExpanded && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="overflow-hidden"
                >
                  <div className="border-t border-orange-700/30 max-h-64 overflow-y-auto">
                    {pastDebts.map((debt) => (
                      <button
                        key={debt.id}
                        onClick={() => openEditModal(debt)}
                        className="w-full px-4 py-3 flex items-center justify-between hover:bg-orange-900/20 transition-colors border-b border-orange-700/20 last:border-b-0"
                      >
                        <div className="text-left">
                          <p className="text-sm text-orange-200">{debt.title}</p>
                          <p className="text-xs text-orange-400/70">
                            {format(new Date(debt.date), 'd MMM yyyy', { locale: tr })}
                          </p>
                        </div>
                        <span className="text-sm font-medium text-orange-400">
                          {formatCurrency(debt.amount, debt.currency || 'TRY')}
                        </span>
                      </button>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>

          {/* Total Due Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-purple-900/30 border border-purple-700/50 rounded-xl p-4"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-400">Toplam Ödenecek</p>
                <p className="text-2xl font-bold text-purple-400 mt-1">
                  {formatCurrency(hasMultipleCurrencies ? totalDueTRY : totalDue, 'TRY')}
                </p>
                <p className="text-xs text-purple-300/70 mt-1">
                  Bu ay + geçmiş borçlar
                </p>
              </div>
              <div className="w-12 h-12 bg-purple-500/20 rounded-xl flex items-center justify-center">
                <Wallet className="w-6 h-6 text-purple-400" />
              </div>
            </div>

            {/* Breakdown */}
            <div className="mt-3 pt-3 border-t border-purple-700/30">
              <div className="flex justify-between text-xs">
                <span className="text-gray-400">Bu ay gider:</span>
                <span className="text-red-400">
                  {formatCurrency(hasMultipleCurrencies ? summary.totalExpenseTRY : summary.totalExpense, 'TRY')}
                </span>
              </div>
              <div className="flex justify-between text-xs mt-1">
                <span className="text-gray-400">Geçmiş borçlar:</span>
                <span className="text-orange-400">
                  {formatCurrency(hasMultipleCurrencies ? pastDebtsTotalTRY : (pastDebtsByCurrency?.TRY || 0), 'TRY')}
                </span>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Status counts and exchange rates */}
      <div className="grid grid-cols-3 md:grid-cols-4 gap-4">
        {statCards.map((card, index) => (
          <motion.div
            key={card.title}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 + index * 0.1 }}
            className="bg-gray-900/50 border border-gray-800 rounded-lg p-3 flex items-center gap-3"
          >
            <div className={`w-8 h-8 ${card.iconBgClass} rounded-lg flex items-center justify-center`}>
              <card.icon className={`w-4 h-4 ${card.textClass}`} />
            </div>
            <div>
              <p className="text-xs text-gray-500">{card.title}</p>
              <p className={`text-lg font-semibold ${card.textClass}`}>{card.count}</p>
            </div>
          </motion.div>
        ))}

        {/* Exchange rates panel */}
        {exchangeRates && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="bg-gray-900/50 border border-gray-800 rounded-lg p-3"
          >
            <div className="flex items-center gap-2 mb-2">
              <RefreshCw className="w-3 h-3 text-gray-500" />
              <p className="text-xs text-gray-500">Döviz Kurları</p>
            </div>
            <div className="flex flex-wrap gap-2">
              {exchangeRates.USD && (
                <span className="text-xs bg-gray-800 px-2 py-1 rounded text-gray-300">
                  $ {exchangeRates.USD.toFixed(2)}
                </span>
              )}
              {exchangeRates.EUR && (
                <span className="text-xs bg-gray-800 px-2 py-1 rounded text-gray-300">
                  € {exchangeRates.EUR.toFixed(2)}
                </span>
              )}
            </div>
            {exchangeRates.source === 'fallback' && (
              <p className="text-xs text-amber-500 mt-1">Tahmini kurlar</p>
            )}
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default CashflowSummary;
